/**
 * The zones a time zone picker offers, and how a typed word finds one.
 *
 * The ids are the browser's own (`timeZoneNames` in `app-dates.ts`), so the list is never older
 * than the runtime that has to accept the value. What the browser cannot say is which country a
 * zone is in, and a rep looks for "Poland" as readily as for "Warsaw". That one fact comes from
 * `countries-and-timezones`, pinned in `package.json`: the zone-to-country table of the tz database
 * and the country names, about 9.5 kB gzipped (measured 2026-10-05).
 *
 * Two things the browser's list gets wrong for a rep, both measured on Chromium 154 on 2026-10-05
 * and both corrected here:
 *
 * - It still spells renamed cities the old way (`Europe/Kiev`, `Asia/Calcutta`,
 *   `America/Buenos_Aires`). A zone is offered under its current id when the list does not carry
 *   that id itself and the runtime accepts it, and is still found by the old spelling.
 * - It leaves out `UTC` and, on Firefox, adds bare abbreviations (`EST`, `Etc/GMT+11`) the host
 *   refuses. `UTC` is added; anything that is not `UTC` or a region/city is dropped.
 *
 * A zone the tz database merged into a neighbour (`Europe/Amsterdam` into `Europe/Brussels`) stays
 * its own entry, because the browser lists both and a Dutch rep looks for Amsterdam.
 *
 * Nothing here reads `Intl`. The names and abbreviations of a zone ("Eastern Time", "EST") come
 * from `zoneWords` in `app-dates.ts` and reach `searchZones` as an argument.
 */
import { getCountry, getTimezone } from "countries-and-timezones";
import { fold } from "./date-entry.ts";

/** One zone a rep may choose. Only `id` is ever stored. */
export type Zone = {
  readonly id: string;
  /** "Warsaw", "New York", "Knox, Indiana". */
  readonly city: string;
  /** The country the zone's own city is in, or `null` for UTC. */
  readonly country: string | null;
  /** Everything a query is matched against, already folded: see `searchZones`. */
  readonly words: ZoneWords;
};

type ZoneWords = {
  readonly city: string;
  readonly id: string;
  /** Earlier spellings of the same place: "kiev" for Kyiv, and its old id. */
  readonly also: readonly string[];
  /** The country's name, its code, and what people call it. */
  readonly country: readonly string[];
};

/** What people call a country that its formal name does not start with. */
const COUNTRY_ALSO: Readonly<Record<string, readonly string[]>> = {
  AE: ["uae", "emirates"],
  CZ: ["czech republic"],
  GB: ["uk", "britain", "great britain", "england", "scotland", "wales"],
  KR: ["korea"],
  NL: ["holland"],
  TR: ["turkey"],
  US: ["usa", "united states", "america"],
};

/*
 * The zone most people mean when a country or an abbreviation has several: "United States" lists
 * New York before Adak, "EST" lists it before Panama. An order for ties and nothing more; a zone
 * that is not here is still found, after these.
 */
const PRINCIPAL: readonly string[] = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Brussels",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Zurich",
  "Europe/Rome",
  "Europe/Vienna",
  "Europe/Prague",
  "Europe/Warsaw",
  "Europe/Stockholm",
  "Europe/Helsinki",
  "Europe/Athens",
  "Europe/Kyiv",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Seoul",
  "Asia/Tokyo",
  "Australia/Perth",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/** A folded word list: separators an id uses become spaces, so "america/new_york" is three words. */
const plain = (text: string): string =>
  fold(text)
    .replace(/[_/,.]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();

const offered = (id: string): boolean =>
  id === "UTC" || (id.includes("/") && !id.startsWith("Etc/"));
const lastPart = (id: string): string => (id.split("/").at(-1) ?? id).replaceAll("_", " ");

/** The current id of a zone the browser lists under an old spelling, or `null` when it is current. */
function renamed(
  id: string,
  listed: ReadonlySet<string>,
  accepts: (id: string) => boolean,
): string | null {
  const known = getTimezone(id);
  const current = known?.aliasOf;
  if (!known?.deprecated || !current || listed.has(current) || !offered(current)) return null;
  // A rename keeps its country. A merge into another country's zone is not a rename.
  const there = getTimezone(current)?.countries ?? [];
  if (!known.countries.some((country) => there.includes(country))) return null;
  return accepts(current) ? current : null;
}

/** One zone in words, from its id alone: for a stored id the browser's own list does not carry. */
export const zoneOf = (id: string): Zone => describe(id, []);

function describe(id: string, also: readonly string[]): Zone {
  const code = getTimezone(id)?.countries[0];
  const country = code ? (getCountry(code)?.name ?? null) : null;
  const parts = id.split("/");
  const place = lastPart(id);
  const within = parts.length === 3 ? (parts[1] ?? "").replaceAll("_", " ") : "";
  const city = within !== "" && within !== country ? `${place}, ${within}` : place;
  return {
    id,
    city,
    country,
    words: {
      city: plain(city),
      id: plain(id),
      also,
      country:
        code && country ? [plain(country), code.toLowerCase(), ...(COUNTRY_ALSO[code] ?? [])] : [],
    },
  };
}

/**
 * The zones to offer, from the ids a runtime lists and its own test of whether it accepts one.
 *
 * Both are arguments so a test can stand in for Chromium, Firefox or Safari; the app passes
 * `timeZoneNames()` and `isTimeZone`. Sorted by city, the order a list is read in.
 */
export function zoneCatalog(
  platform: readonly string[],
  accepts: (id: string) => boolean,
): readonly Zone[] {
  const listed = new Set(platform);
  const also = new Map<string, string[]>();
  for (const raw of ["UTC", ...platform]) {
    if (!offered(raw)) continue;
    const current = renamed(raw, listed, accepts);
    const spellings = also.get(current ?? raw) ?? [];
    if (current !== null) spellings.push(plain(lastPart(raw)), plain(raw));
    also.set(current ?? raw, spellings);
  }
  return [...also]
    .map(([id, spellings]) => describe(id, spellings))
    .sort((a, b) => a.words.city.localeCompare(b.words.city, "en"));
}

/** The zone an id names, in any case, by its current or an earlier spelling. */
export function findZone(catalog: readonly Zone[], id: string): Zone | undefined {
  const wanted = plain(id);
  if (wanted === "") return undefined;
  return (
    catalog.find((zone) => zone.words.id === wanted) ??
    catalog.find((zone) => zone.words.also.includes(wanted))
  );
}

export type ZoneSearch = {
  /** The zone's own name and abbreviations, from `zoneWords`. Left out, only places are searched. */
  readonly words?: (id: string) => readonly string[];
};

const startsAWord = (text: string, query: string): boolean =>
  text.split(" ").some((word) => word.startsWith(query));

/**
 * How well a zone answers a query, lower being better, or `null` when it does not.
 *
 * A whole word beats the start of one, and a place beats a country beats a zone's name: "india" is
 * India before it is Indiana, and "war" is Warsaw before it is anything in a "Western ... Time".
 */
function rank(zone: Zone, query: string, names: readonly string[]): number | null {
  const { city, id, also, country } = zone.words;
  if (query === city || query === id || also.includes(query)) return 0;
  if (country.includes(query) || names.includes(query)) return 1;
  if (city.startsWith(query) || id.startsWith(query) || also.some((word) => word.startsWith(query)))
    return 2;
  if (startsAWord(city, query)) return 3;
  if (country.some((word) => word.startsWith(query))) return 4;
  if (country.some((word) => startsAWord(word, query))) return 5;
  if (names.some((word) => word.startsWith(query) || startsAWord(word, query))) return 6;
  if (id.includes(query)) return 7;
  const everything = [city, id, ...also, ...country, ...names].join(" ");
  const parts = query.split(" ");
  return parts.length > 1 && parts.every((part) => startsAWord(everything, part)) ? 8 : null;
}

/** The zones a typed query finds, best first. An empty query finds none: the caller lists them. */
export function searchZones(
  catalog: readonly Zone[],
  query: string,
  search: ZoneSearch = {},
): readonly Zone[] {
  const wanted = plain(query);
  if (wanted === "") return [];
  const principal = (zone: Zone): number => {
    const index = PRINCIPAL.indexOf(zone.id);
    return index === -1 ? PRINCIPAL.length : index;
  };
  return catalog
    .flatMap((zone) => {
      const score = rank(zone, wanted, (search.words?.(zone.id) ?? []).map(plain));
      return score === null ? [] : [{ zone, score }];
    })
    .sort(
      (a, b) =>
        a.score - b.score ||
        principal(a.zone) - principal(b.zone) ||
        a.zone.words.city.localeCompare(b.zone.words.city, "en"),
    )
    .map(({ zone }) => zone);
}
